/**
 * The GST bill behind one bank payment.
 *
 * Opened under the line it is about. The Book Keeper types what is on the
 * supplier's invoice -- GSTIN, number, date, taxable value and tax -- and the
 * line is booked through that bill: the expense at taxable value, the tax as
 * credit to claim, the bank once. The GSTIN decides the split: a supplier in
 * our state charges CGST and SGST, one elsewhere IGST, so only those boxes are
 * offered.
 *
 * The invoice itself is owed within seven working days of the payment, and
 * the credit cannot be claimed until it is attached -- so the attach slot is
 * right here once the bill is saved.
 */

import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import Attachments from '../components/Attachments'
import { ErrorNote, Field, Note } from '../components/ui'

export interface GstBill {
  invoice_id: string
  reference: string
  status: string
  vendor_name: string
  supplier_invoice_no: string
  tax_paise: number
  booked: boolean
  waiting_for: string | null
  invoice_state: string
  invoice_due_on: string
  working_days_overdue: number
}

interface Supplier {
  gstin: string
  state_code: string
  state_name: string
  pan: string
  intra_state: boolean | null
  vendor_id: string | null
  vendor_name: string | null
  vendor_status: string | null
}

interface Line {
  id: string
  txn_date: string
  amount_paise: number
  narration_raw: string
  gst_bill: GstBill | null
  //: Set while the bank debit control holds the payment.
  posting_blocked: string | null
  unplanned_outstanding: string | null
  unplanned_accepted_by: string | null
  control_state: string
}

function toPaise(text: string): number {
  const clean = text.replace(/[^0-9.]/g, '')
  if (!clean) return 0
  return Math.round(Number(clean) * 100)
}

const day = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
  })

//: One line under the badge on the Banking list.
export function gstBillNote(bill: GstBill): { text: string; tone: string } {
  if (!bill.booked)
    return {
      text: `${bill.reference} · waiting for ${bill.waiting_for ?? 'its supplier'}`,
      tone: 'advisory',
    }
  if (bill.invoice_state === 'attached')
    return { text: `${bill.reference} · invoice attached`, tone: 'pass' }
  if (bill.invoice_state === 'overdue')
    return {
      text: `${bill.reference} · invoice overdue since ${day(bill.invoice_due_on)}`,
      tone: 'blocking',
    }
  return {
    text: `${bill.reference} · invoice due by ${day(bill.invoice_due_on)}`,
    tone: 'advisory',
  }
}

export default function GstBillPanel({
  txn,
  onDone,
  onCancel,
}: {
  txn: Line
  onDone: () => void
  onCancel: () => void
}) {
  const { can } = useAuth()
  const [gstin, setGstin] = useState('')
  const [name, setName] = useState('')
  const [number, setNumber] = useState('')
  const [date, setDate] = useState(txn.txn_date)
  const [taxable, setTaxable] = useState('')
  const [cgst, setCgst] = useState('')
  const [sgst, setSgst] = useState('')
  const [sgstTouched, setSgstTouched] = useState(false)
  const [igst, setIgst] = useState('')
  const [itc, setItc] = useState(true)
  const [what, setWhat] = useState('')
  const [saved, setSaved] = useState<GstBill | null>(txn.gst_bill)

  const typed = gstin.trim().toUpperCase().replace(/\s/g, '')
  const supplier = useQuery({
    queryKey: ['gst-bill-supplier', txn.id, typed],
    queryFn: () =>
      api.get<Supplier>(
        `/banking/transactions/${txn.id}/gst-bill/supplier?gstin=${encodeURIComponent(typed)}`,
      ),
    enabled: typed.length === 15 && saved === null,
    retry: false,
  })

  const record = useMutation({
    mutationFn: () =>
      api.post<Line>(`/banking/transactions/${txn.id}/gst-bill`, {
        gstin: typed,
        supplier_invoice_no: number.trim(),
        invoice_date: date,
        taxable_paise: toPaise(taxable),
        cgst_paise: local === false ? 0 : toPaise(cgst),
        sgst_paise: local === false ? 0 : toPaise(sgst),
        igst_paise: local === true ? 0 : toPaise(igst),
        itc_eligible: itc,
        supplier_name: known ? null : name.trim() || null,
        description: what.trim() || null,
      }),
    onSuccess: (line) => {
      setSaved(line.gst_bill)
      onDone()
    },
  })
  const book = useMutation({
    mutationFn: () => api.post<Line>(`/banking/transactions/${txn.id}/gst-bill/book`, {}),
    onSuccess: (line) => {
      setSaved(line.gst_bill)
      onDone()
    },
  })

  if (saved !== null) {
    return (
      <div className="inset">
        <strong>
          {saved.reference} · {saved.vendor_name} invoice {saved.supplier_invoice_no}
        </strong>
        <ErrorNote error={book.error} />
        {saved.booked ? (
          <Note kind={saved.invoice_state === 'overdue' ? 'warn' : 'info'}>
            Booked: the expense at taxable value and {formatInr(saved.tax_paise)} of GST as credit.
            Attach the supplier’s invoice by {day(saved.invoice_due_on)} — until it is attached, the
            credit cannot be claimed.
          </Note>
        ) : saved.waiting_for ? (
          <Note kind="info">
            Saved, and waiting for {saved.waiting_for}. Nothing is booked until then; the invoice
            can be attached now.
          </Note>
        ) : (
          <Note kind="info">The supplier is approved — the bill can be booked now.</Note>
        )}
        <Attachments
          subjectType="vendor_invoice"
          subjectId={saved.invoice_id}
          kind="invoice"
          canAttach={can('ap.invoice.create')}
          label="Attach the supplier’s invoice"
        />
        <div className="actions" style={{ marginTop: 8 }}>
          {!saved.booked && saved.waiting_for === null && (
            <button className="primary" disabled={book.isPending} onClick={() => book.mutate()}>
              Book it
            </button>
          )}
          <button onClick={onCancel}>Close</button>
        </div>
      </div>
    )
  }

  const found = supplier.data
  const known = Boolean(found?.vendor_id)
  const local = found ? found.intra_state : null
  const tax =
    (local === false ? 0 : toPaise(cgst) + toPaise(sgst)) + (local === true ? 0 : toPaise(igst))
  const parts = toPaise(taxable) + tax
  const gap = txn.amount_paise - parts
  const agrees = toPaise(taxable) > 0 && Math.abs(gap) <= 99
  const ready =
    found !== undefined &&
    number.trim() !== '' &&
    toPaise(taxable) > 0 &&
    tax > 0 &&
    agrees &&
    (known || name.trim() !== '')

  return (
    <div className="inset">
      <strong>
        GST invoice behind {formatInr(txn.amount_paise)} paid on {txn.txn_date}
      </strong>
      <ErrorNote error={record.error} />
      <p className="faint mono" style={{ fontSize: 12, margin: '4px 0 8px' }}>
        {txn.narration_raw}
      </p>
      {!txn.unplanned_accepted_by &&
        (txn.posting_blocked ||
          txn.unplanned_outstanding ||
          txn.control_state === 'unapproved') && (
          <Note kind="info">
            This payment has no approval on record, so the bill goes to the Finance Head. Approving
            the bill approves the payment; then press Book it here.
          </Note>
        )}
      <div className="row">
        <Field label="Supplier’s GSTIN" caption="15 characters, from their invoice">
          <input
            value={gstin}
            maxLength={20}
            onChange={(e) => setGstin(e.target.value)}
            style={{ textTransform: 'uppercase' }}
          />
        </Field>
        {found && known && (
          <Field label="Supplier" caption={found.state_name}>
            <div style={{ paddingTop: 6 }}>
              {found.vendor_name}
              {found.vendor_status !== 'approved' && (
                <span className="faint"> · waiting for the Finance Head</span>
              )}
            </div>
          </Field>
        )}
        {found && !known && (
          <Field
            label="Supplier’s name"
            caption={`new · ${found.state_name} · the Finance Head approves them once`}
          >
            <input value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
          </Field>
        )}
      </div>
      {supplier.error && <ErrorNote error={supplier.error} />}
      <div className="row">
        <Field label="Invoice number" caption="as printed">
          <input value={number} maxLength={60} onChange={(e) => setNumber(e.target.value)} />
        </Field>
        <Field label="Invoice date">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <Field label="Taxable value (₹)">
          <input inputMode="decimal" value={taxable} onChange={(e) => setTaxable(e.target.value)} />
        </Field>
      </div>
      <div className="row">
        {local !== false && (
          <>
            <Field label="CGST (₹)">
              <input
                inputMode="decimal"
                value={cgst}
                onChange={(e) => {
                  setCgst(e.target.value)
                  if (!sgstTouched) setSgst(e.target.value)
                }}
              />
            </Field>
            <Field label="SGST (₹)" caption="always the same as CGST">
              <input
                inputMode="decimal"
                value={sgst}
                onChange={(e) => {
                  setSgst(e.target.value)
                  setSgstTouched(true)
                }}
              />
            </Field>
          </>
        )}
        {local !== true && (
          <Field label="IGST (₹)" caption={local === false ? 'supplier in another state' : ''}>
            <input inputMode="decimal" value={igst} onChange={(e) => setIgst(e.target.value)} />
          </Field>
        )}
        <Field label="What for" caption="optional; the category is used otherwise">
          <input value={what} maxLength={300} onChange={(e) => setWhat(e.target.value)} />
        </Field>
      </div>
      {toPaise(taxable) > 0 && (
        <Note kind={agrees ? 'ok' : 'warn'}>
          Taxable {formatInr(toPaise(taxable))} + GST {formatInr(tax)} = {formatInr(parts)}
          {agrees
            ? gap === 0
              ? ' — exactly what the bank paid.'
              : ` — ${formatInr(Math.abs(gap))} of rounding against the bank.`
            : ` — the bank paid ${formatInr(txn.amount_paise)}. A bill recorded here is for exactly what was paid.`}
        </Note>
      )}
      <label className="appr-check">
        <input type="checkbox" checked={itc} onChange={(e) => setItc(e.target.checked)} /> Input
        credit can be claimed on this{' '}
        <span className="faint">
          (untick for blocked credit — food, staff welfare, personal use — and the tax becomes part
          of the expense)
        </span>
      </label>
      <div className="actions">
        <button
          className="primary"
          disabled={record.isPending || !ready}
          onClick={() => record.mutate()}
        >
          {known && found?.vendor_status === 'approved' ? 'Record and book' : 'Record'}
        </button>
        <button onClick={onCancel}>Cancel</button>
      </div>
      <p className="faint" style={{ margin: '8px 0 0' }}>
        Attach the supplier’s invoice within 7 working days of the payment (Sundays not counted).
        Until it is attached, the GST on it cannot be claimed.
      </p>
    </div>
  )
}
